// Original legal content for Renn (WorthGrid Technologies, LLC).
// Plain-language templates written for a financial education & decision-support product.

const UPDATED = "Last updated: January 2026";
const COMPANY = "WorthGrid Technologies, LLC";

export const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    updated: UPDATED,
    intro:
      `This Privacy Policy explains how ${COMPANY} ("WorthGrid," "we," "us") collects, uses, and protects information when you use Renn. We built Renn to help you organize your finances and understand your next steps \u2014 not to harvest or sell your data.`,
    sections: [
      {
        heading: "Information you provide",
        body: [
          "Renn collects only the information you choose to enter. This includes account details such as your name and email, and the financial details you add to your profile \u2014 for example income, expenses, savings, debt, credit, protection, military compensation, and goals.",
          "Renn does not require bank passwords, does not collect Social Security numbers, and does not collect complete account numbers.",
        ],
      },
      {
        heading: "How we use information",
        body: [
          "We use the information you provide to calculate your WorthScore, generate your Renn Moves and Missions, power features such as Ask Renn and the Scenario Planner, and keep your plan current through weekly check-ins.",
          "We use limited technical and usage data to operate, secure, and improve the product.",
        ],
      },
      {
        heading: "What we do not do",
        body: [
          "We do not sell your personal financial information for advertising. We do not share your financial profile with third parties for their own marketing.",
        ],
      },
      {
        heading: "Your controls",
        body: [
          "You can export your data, manage notification preferences, and delete your account and associated information directly in the app at any time.",
        ],
      },
      {
        heading: "Security",
        body: [
          "We use secure authentication and encrypted connections to protect information in transit. No system is perfectly secure, and we continue to invest in safeguards as Renn grows.",
        ],
      },
      {
        heading: "Contact",
        body: [
          `Questions about this policy can be sent to our support team. This document will be updated as ${COMPANY} and Renn evolve.`,
        ],
      },
    ],
  },

  terms: {
    title: "Terms of Use",
    updated: UPDATED,
    intro:
      `These Terms of Use govern your access to and use of Renn, a product of ${COMPANY}. By using Renn you agree to these terms.`,
    sections: [
      {
        heading: "The service",
        body: [
          "Renn provides financial education, organization, planning, and decision-support tools, including WorthScore, Renn Moves, Missions, Ask Renn, and the Scenario Planner. Renn is currently offered as a private beta and may change as we improve it.",
        ],
      },
      {
        heading: "Not professional advice",
        body: [
          "Renn is not a bank, lender, broker, registered investment adviser, accounting firm, law firm, insurance provider, or credit-repair company. Renn does not provide individualized legal, tax, accounting, insurance, or investment advice. You are responsible for the decisions you make.",
        ],
      },
      {
        heading: "Your account",
        body: [
          "You are responsible for maintaining the confidentiality of your login and for the accuracy of the information you enter. Estimates and projections depend on the information you provide.",
        ],
      },
      {
        heading: "Acceptable use",
        body: [
          "You agree to use Renn lawfully and not to interfere with, disrupt, or attempt to gain unauthorized access to the service or other users\u2019 data.",
        ],
      },
      {
        heading: "Beta program",
        body: [
          "Beta features are provided as-is and may be modified or discontinued. Participation in the beta is free, and any promotional benefits are described at the time they are offered.",
        ],
      },
      {
        heading: "Changes",
        body: [
          "We may update these terms from time to time. Continued use of Renn after changes take effect constitutes acceptance of the updated terms.",
        ],
      },
    ],
  },

  "financial-disclaimer": {
    title: "Financial Disclaimer",
    updated: UPDATED,
    intro:
      "Renn is designed to help you understand and organize your finances. It is important to be clear about what Renn is and is not.",
    sections: [
      {
        heading: "Educational and organizational purpose",
        body: [
          "All content, scores, priorities, and projections in Renn \u2014 including WorthScore, Renn Moves, Missions, Ask Renn responses, and Scenario Planner outputs \u2014 are provided for educational, organizational, and decision-support purposes only.",
        ],
      },
      {
        heading: "Not individualized advice",
        body: [
          "Renn does not provide individualized legal, tax, accounting, insurance, or investment advice, and nothing in Renn should be interpreted as a recommendation to buy, sell, or hold any specific financial product or security.",
          "Before making significant financial decisions, consider consulting a qualified professional who can review your complete situation.",
        ],
      },
      {
        heading: "Estimates and projections",
        body: [
          "Projections, impact estimates, and scenario results are illustrations based on the information you provide and reasonable assumptions. Actual outcomes will vary. Example figures shown on our website are sample data and do not represent real user results.",
        ],
      },
      {
        heading: "No guarantees",
        body: [
          "Using Renn does not guarantee any particular financial result. Your results depend on your circumstances and the decisions you choose to make.",
        ],
      },
    ],
  },

  "ai-disclosure": {
    title: "AI Disclosure",
    updated: UPDATED,
    intro:
      "Renn uses automated systems, including artificial intelligence, to help explain your plan and support your decisions. This disclosure describes how those systems are used.",
    sections: [
      {
        heading: "How AI is used",
        body: [
          "Features such as Ask Renn use automated systems, informed by the financial profile you save, to explain priorities, compare options, and describe the reasoning behind your plan in clear language.",
        ],
      },
      {
        heading: "Rules and calculations",
        body: [
          "WorthScore calculations and recommendation rules are governed by defined logic and safety standards. Choosing a Renn communication personality changes tone only \u2014 it does not change the underlying calculations, recommendation rules, or safety standards.",
        ],
      },
      {
        heading: "You stay in control",
        body: [
          "AI-generated explanations are a starting point for understanding, not a directive. You remain in control of every financial decision you make.",
        ],
      },
      {
        heading: "Limitations",
        body: [
          "Automated systems can be incomplete or occasionally incorrect. Renn\u2019s AI outputs are educational and do not constitute individualized legal, tax, accounting, insurance, or investment advice.",
        ],
      },
    ],
  },
};
